import { LazyComponent } from './lazy'

// Shared across area router, drawer, and sheet services.
export type ComponentType = CustomElementConstructor | string | HTMLElement | LazyComponent<any>

export type RouteAction = {
	component: ComponentType
	area: string
	state?: Record<string, unknown>
	params?: Record<string, unknown>
	props?: Record<string, unknown> // Alias for params
	historyStrategy?: THistoryStrategy
	clearQueryParams?: string[] | boolean | null
	animationDuration?: number // Animation duration in milliseconds (0 = instant/no animation, default = 150ms)
	// The whole address this push writes, e.g. '/order-tracking' — not a prefix.
	// The router writes it verbatim (a query or hash spelled here is kept, a param
	// in it winning over one the previous URL carried) and appends neither the
	// encoded-areas segment nor a prettyURL component name to it, so it is the way
	// to give an area a readable, shareable URL.
	//
	// Two obligations come with it. A destination reached this way must declare a
	// route whose `when` equals a segment of the path ('/admin' → when="admin"),
	// because a COLD load of that address has no history.state and is resolved by
	// the area matching path segments against its `when` keys — without it the area
	// falls to its `.default`. And once an app names its own paths it should name
	// one on every push: a push without `path` derives its address from the current
	// URL and appends the encoded-areas segment to it.
	//
	// A different path mounts a different page only when both navigations name one:
	// a side that names no path matches any path. Carry an entity's identity in
	// `params` or `state`, never in `path` alone.
	path?: string
	_source?: 'programmatic' | 'browser' | 'initial' // Internal use only
	_sameDocument?: boolean // Internal use only
}

export type ActiveRoute = {
	component: string
	area: string
	// The declared route's `when` name, when this route was matched against one —
	// the identity space a departure compares by preference, since `when` is known
	// synchronously for a lazy-loaded route while its resolved tag is not.
	when?: string
	state?: Record<string, unknown>
	params?: Record<string, unknown>
	props?: Record<string, unknown>
}

// Emitted by AreaService.departures$(areaName) the instant an incoming push's
// target differs from the area's currently active route — reported as resolved
// tags where known, the declared `when` name otherwise. The same/different
// decision itself compares `when` to `when` when both sides declare one,
// falling back to tag-to-tag only when one side doesn't — `when` and a tag
// name are different identity spaces and are never compared across sides. A
// same-target (param-only) push never produces one.
export type AreaDeparture = {
	area: string
	from: string
	to: string
}

export interface AreaSubscription {
	readonly url: URL
	readonly url$: import('rxjs').Observable<URL>
	on(areaName: string, skipCurrent?: boolean): import('rxjs').Observable<ActiveRoute>
	all(skipCurrent?: boolean): import('rxjs').Observable<Map<string, ActiveRoute>>
	getState<T = unknown>(areaName: string): import('rxjs').Observable<T>
	params<T extends Record<string, unknown> = Record<string, unknown>>(areaName: string): import('rxjs').Observable<T>
	param<T = unknown>(areaName: string, key: string): import('rxjs').Observable<T>
	props<T extends Record<string, unknown> = Record<string, unknown>>(areaName: string): import('rxjs').Observable<T>
	prop<T = unknown>(areaName: string, key: string): import('rxjs').Observable<T>
}

export type THistoryStrategy = 'push' | 'replace' | 'pop' | 'silent'

export enum HISTORY_STRATEGY {
	push = 'push',
	replace = 'replace',
	pop = 'pop',
	silent = 'silent',
}

export interface SchmancyHistoryState {
	schmancyAreas: Record<string, ActiveRoute>
	[key: string]: any // Allow other apps to store additional state
}
